import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { HttpRequestError } from './http-error.model';

type HttpOptions = {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
  context?: HttpContext;
  reportProgress?: boolean;
  responseType?: any;
  withCredentials?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly http = inject(HttpClient);

  get<T>(url: string, options?: HttpOptions): Observable<T> {
    return this.http.get<T>(url, options).pipe(catchError(this.handleError.bind(this)));
  }

  post<T>(url: string, body: any, options?: HttpOptions): Observable<T> {
    return this.http.post<T>(url, body, options).pipe(catchError(this.handleError.bind(this)));
  }

  put<T>(url: string, body: any, options?: HttpOptions): Observable<T> {
    return this.http.put<T>(url, body, options).pipe(catchError(this.handleError.bind(this)));
  }

  patch<T>(url: string, body: any, options?: HttpOptions): Observable<T> {
    return this.http.patch<T>(url, body, options).pipe(catchError(this.handleError.bind(this)));
  }

  delete<T>(url: string, options?: HttpOptions): Observable<T> {
    return this.http.delete<T>(url, options).pipe(catchError(this.handleError.bind(this)));
  }

  private handleError(error: HttpErrorResponse | Error | any): Observable<never> {
    // abortSignal errors are handled separately
    if (error.name === 'AbortError' || error.error?.name === 'AbortError') {
      return throwError(() => error);
    }

    let errorMessage = 'An unknown error occurred!';
    let status: number | undefined;
    let statusText: string | undefined;
    let url: string | undefined;

    if (error instanceof HttpErrorResponse) {
      status = error.status;
      statusText = error.statusText;
      url = error.url ?? undefined;

      // status 0 indicates a network or client error (CORS, offline, etc.)
      if (error.status === 0) {
        errorMessage = `Client-side/Network error: ${error.message}`;
      } else {
        errorMessage = `Server-side error: ${error.status} - ${error.message}`;
      }
    } else {
      errorMessage = error?.message || errorMessage;
    }

    if (isDevMode()) {
      console.error('[HttpService Error]:', errorMessage, error);
    }

    // The HTTP state is preserved: consumers (the api interceptor, the auth forms) rely on it.
    return throwError(() => new HttpRequestError(errorMessage, status, statusText, url));
  }
}
